"use client";

import Link from "next/link";

import { L } from "@/extensions/lida/lida";
import { T } from "@/lib/i18n/T";
import type { ProductCard } from "@/lib/catalog/types";
import { formatMoney } from "@/lib/catalog/format";

/**
 * Catalog tile — ported from the legacy `ProductTile`, on the real backend.
 *
 * Two deliberate differences from the old one:
 *  - No hover image swap. The old tile crossfaded `images[0]`/`images[1]`, but
 *    `ProductCard` carries a single image on purpose (a list of 24 tiles would
 *    otherwise pull 48 images). A scale-up on hover keeps the affordance.
 *  - No client-side currency conversion. The backend returns the price already
 *    in the requested currency, so there is nothing to convert and no chance of
 *    showing a number the checkout then contradicts.
 *
 * The title goes through WorldLida, not the message catalog: it is supplier
 * copy that only exists at runtime, so no catalog could ever hold it. On `en`
 * `<L>` short-circuits and renders the source string untouched.
 */

export function ProductTile({
  product,
  href,
  locale,
}: {
  product: ProductCard;
  href: string;
  locale: string;
}) {
  const price = formatMoney(locale, product.price);
  const compareAt = formatMoney(locale, product.compare_at);

  return (
    <Link href={href} className="rb-tile">
      <div className="rb-tile-image">
        {product.image ? (
          /* eslint-disable-next-line @next/next/no-img-element -- supplier imagery
             arrives via the backend proxy already resized; next/image would add a
             second resize hop for no gain, and it must work in the static export. */
          <img src={product.image} alt="" loading="lazy" decoding="async" />
        ) : (
          <span className="rb-tile-image-empty" aria-hidden="true" />
        )}
        {product.discount_percent != null && product.discount_percent > 0 && (
          <span className="rb-tile-badge">−{product.discount_percent}%</span>
        )}
      </div>

      <div className="rb-tile-body">
        <p className="rb-tile-title"><L text={product.title} /></p>
        {product.brand && <h5 className="rb-tile-brand">{product.brand}</h5>}

        <div className="rb-tile-price">
          <h5>
            {price ?? <T id="catalog.priceUnavailable" />}
            {compareAt && <s className="rb-tile-compare">{compareAt}</s>}
          </h5>
          {product.rating.count > 0 && product.rating.score != null && (
            <h6 className="rb-tile-rating">
              ★ {product.rating.score.toFixed(1)}{" "}
              <span>({product.rating.count})</span>
            </h6>
          )}
        </div>
      </div>
    </Link>
  );
}

/** Placeholder tile shown while a page of results is in flight. */
export function ProductTileSkeleton() {
  return (
    <div className="rb-tile rb-tile--loading" aria-hidden="true">
      <div className="rb-tile-image" />
      <div className="rb-tile-body">
        <span className="rb-tile-skel rb-tile-skel--title" />
        <span className="rb-tile-skel rb-tile-skel--price" />
      </div>
    </div>
  );
}
